import type { ReactNode } from "react";

import { cloneElement, isValidElement } from "react";

/**
 * Recursively traverses React children and replaces all leaf text nodes with
 * the given replacement string, preserving wrapping elements (e.g. <strong>,
 * <em>). This allows resolved placeholder values to inherit decorator marks
 * (bold, italic, etc.) that the editor applied to the annotated span.
 *
 * Only preserves marks when the annotated content is a single, uniformly-
 * wrapped span. If children is an array with more than one element (the
 * annotation spans multiple spans with potentially different marks), the
 * resolved value is returned as plain text to avoid duplicating or mixing
 * formatting incorrectly.
 */
function replaceChildrenText(children: ReactNode, text: string): ReactNode {
  if (typeof children === "string") {
    return text;
  }
  if (isValidElement<{ children?: ReactNode }>(children)) {
    return cloneElement(
      children,
      {},
      replaceChildrenText(children.props.children, text),
    );
  }
  if (Array.isArray(children)) {
    if (children.length === 1) {
      return replaceChildrenText(children[0], text);
    }
    return text;
  }
  return text;
}

export { replaceChildrenText };
